// Shared lifecycle-status classification used by both the Contacts pipeline board
// (GeneralCustomers) and the contact profile (CustomerDetails) so the two views
// always agree on which status a contact falls into. The vocabulary mirrors the
// profile's Status field: active / at risk / dormant / lapsed, plus vip and new.

import { isAfter, subDays } from "date-fns";

export type PipelineStage = "new" | "active" | "at-risk" | "dormant" | "lapsed" | "vip";

// Lifecycle thresholds, measured by days since the contact's last activity.
// Mirrors the profile tooltip: active (<30d), at risk (30–60d),
// dormant (60–180d), lapsed (≥180d).
export const ACTIVE_MAX_DAYS = 30;
export const AT_RISK_MAX_DAYS = 60;
export const DORMANT_MAX_DAYS = 180;

// Recent spend is measured over this window (matches the VIP logic).
export const RECENT_WINDOW_DAYS = 60;

export const STAGE_META: Record<PipelineStage, { label: string; hint: string; badge: string }> = {
  new: { label: "New", hint: "No activity yet", badge: "bg-sky-500/15 text-sky-700 border-sky-500/30" },
  active: { label: "Active", hint: "Active in the last 30 days", badge: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  "at-risk": { label: "At risk", hint: "Quiet for 30–60 days", badge: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  dormant: { label: "Dormant", hint: "Inactive for 60–180 days", badge: "bg-orange-500/15 text-orange-700 border-orange-500/30" },
  lapsed: { label: "Lapsed", hint: "No activity for 180+ days", badge: "bg-rose-500/15 text-rose-700 border-rose-500/30" },
  vip: { label: "VIP", hint: "Top 20% spenders (last 60 days)", badge: "bg-violet-500/15 text-violet-700 border-violet-500/30" },
};

export interface StageInputs {
  recent_order_value: number;
  total_order_value: number;
  created_at: string;
  // Most recent activity timestamp (e.g. last booking date). Null when the
  // contact has no recorded activity yet.
  last_activity_at?: string | null;
}

// VIP threshold = the 80th-percentile of recent spend among contacts who
// actually spent. Returns Infinity when no one has spent (so nobody is VIP).
export function computeVipThreshold(recentSpends: number[]): number {
  const sorted = recentSpends.filter((v) => v > 0).sort((a, b) => b - a);
  if (sorted.length === 0) return Infinity;
  const idx = Math.min(Math.ceil(sorted.length * 0.2) - 1, sorted.length - 1);
  return sorted[Math.max(0, idx)];
}

export function classifyPipelineStage(c: StageInputs, vipThreshold: number): PipelineStage {
  // VIP: a recent top-20% spender outranks the recency-based statuses.
  if (c.recent_order_value > 0 && c.recent_order_value >= vipThreshold) {
    return "vip";
  }

  // No activity recorded at all → still a new contact.
  if (!c.last_activity_at && c.total_order_value <= 0) {
    return "new";
  }

  const last = c.last_activity_at ? new Date(c.last_activity_at) : new Date(c.created_at);
  const now = new Date();
  if (isAfter(last, subDays(now, ACTIVE_MAX_DAYS))) return "active";
  if (isAfter(last, subDays(now, AT_RISK_MAX_DAYS))) return "at-risk";
  if (isAfter(last, subDays(now, DORMANT_MAX_DAYS))) return "dormant";
  return "lapsed";
}
